import React, { useMemo } from 'react';

import { cn } from '@epoch-ui/lib/utils';
import { formatDateInTimezone, guessTimezone } from 'utils/dateTimeUtils';
import { Banner, BannerTitle, BannerClose } from '@epoch-ui/components/Banner';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { usePublishErrorsModal } from '@event-editor/hooks/eventEditorState';
import { EventPublishScheduleStatus } from 'types/event.types';

type EditorEventState =
  | 'draft'
  | 'ended'
  | 'cancelled'
  | 'publishScheduled'
  | 'publishError'
  | 'scheduledPublishingError';

const EventStateClassNamesMap: Record<EditorEventState, string> = {
  draft: 'tw-bg-brand-accent',
  ended: 'tw-bg-secondary',
  cancelled: 'tw-bg-destructive',
  publishScheduled: '',
  publishError: 'tw-bg-destructive',
  scheduledPublishingError: 'tw-bg-destructive'
};

interface EditorEventStateBannerProps extends React.HTMLAttributes<HTMLDivElement> {}

const EditorEventStateBanner = React.forwardRef<HTMLDivElement, EditorEventStateBannerProps>(
  ({ className, ...props }, ref) => {
    const event = useEditableEvent();
    const {
      isBannerShown: isPublishErrorsBannerShown,
      toggleErrorsDetailsModal: togglePublishErrorsDetailsModal
    } = usePublishErrorsModal();

    const eventState: EditorEventState | undefined = useMemo(() => {
      // Check publish error first (highest priority)
      if (isPublishErrorsBannerShown) {
        return 'publishError';
      }

      if (event.scheduledDeletionAt) {
        return 'cancelled';
      }

      if (event.hasEnded) {
        return 'ended';
      }

      if (!event.isPublished) {
        if (event.publishSchedule?.status === EventPublishScheduleStatus.SCHEDULED) {
          return 'publishScheduled';
        }

        if (event.publishSchedule?.status === EventPublishScheduleStatus.FAILED) {
          return 'scheduledPublishingError';
        }

        return 'draft';
      }

      return undefined;
    }, [isPublishErrorsBannerShown, event]);

    if (!eventState || eventState === 'publishScheduled') {
      return null;
    }

    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          'tw-sticky tw-left-0 tw-top-0 tw-z-20 tw-w-full tw-border-0 tw-border-b tw-border-solid tw-text-muted-foreground',
          className
        )}
      >
        <Banner
          className={cn(
            'tw-rounded-none tw-text-center tw-font-medium tw-leading-5 tw-text-muted-foreground',
            EventStateClassNamesMap[eventState]
          )}
        >
          {eventState === 'cancelled' && (
            <BannerTitle>
              {/* This event is canceled. It will be removed from Epoch on Sun, Jun 18, 2025 at 5:32pm PDT. Learn more. */}
              This event is canceled. It will be removed from Epoch on{' '}
              {formatEventCancellationSchedule(event.scheduledDeletionAt!, event.timezoneCode)}.{' '}
              <a
                target="_blank"
                className="tw-text-muted-foreground tw-underline"
                rel="noopener noreferrer"
                href="https://help.epochapp.com/articles/canceled-events"
              >
                Learn more
              </a>
              .
            </BannerTitle>
          )}
          {eventState === 'ended' && <BannerTitle>This event has ended.</BannerTitle>}
          {eventState === 'draft' && (
            <>
              <BannerTitle>
                This is a draft event. If you&apos;re ready to go live, publish the event.{' '}
                <a
                  target="_blank"
                  className="tw-text-muted-foreground tw-underline"
                  rel="noopener noreferrer"
                  href="http://help.epochapp.com/articles/what-are-the-different-event-states"
                >
                  Learn more
                </a>
                .
              </BannerTitle>
              <BannerClose />
            </>
          )}
          {eventState === 'publishError' && (
            <BannerTitle className="tw-text-negative-foreground">
              We couldn&apos;t publish this event.{' '}
              <button
                type="button"
                onClick={togglePublishErrorsDetailsModal}
                className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-negative-foreground tw-underline"
              >
                See details
              </button>
            </BannerTitle>
          )}

          {eventState === 'scheduledPublishingError' && (
            <BannerTitle className="tw-text-negative-foreground">
              We couldn&apos;t publish this event at the scheduled time. Please try again.
            </BannerTitle>
          )}
        </Banner>
      </div>
    );
  }
);

export { EditorEventStateBanner };

const formatEventCancellationSchedule = (dateTimeString: string, timezoneCode?: string): string => {
  const date = new Date(dateTimeString);

  return `${formatDateInTimezone(
    date,
    timezoneCode ?? guessTimezone(),
    'ddd, MMM D, YYYY'
  )} at ${formatDateInTimezone(date, timezoneCode ?? guessTimezone(), 'h:mma z')}`;
};
